import { useCallback, useRef, TouchEvent } from 'react';

interface UseSwipeNavigationProps {
  onSwipeLeft?: () => void;
  onSwipeRight?: () => void;
  threshold?: number;
  onDrag?: (dragX: number) => void;
}

export const useSwipeNavigation = ({
  onSwipeLeft,
  onSwipeRight,
  threshold = 50,
  onDrag,
}: UseSwipeNavigationProps) => {
  const touchStartXRef = useRef<number>(0);
  const currentXRef = useRef<number>(0);

  const handleTouchStart = useCallback((e: TouchEvent) => {
    touchStartXRef.current = e.touches[0].clientX;
    currentXRef.current = e.touches[0].clientX;
  }, []);

  const handleTouchMove = useCallback(
    (e: TouchEvent) => {
      currentXRef.current = e.touches[0].clientX;
      const dragX = currentXRef.current - touchStartXRef.current;
      onDrag?.(dragX);
    },
    [onDrag],
  );

  const handleTouchEnd = useCallback(
    (e: TouchEvent) => {
      const touchEndX = e.changedTouches[0].clientX;
      const diffX = touchStartXRef.current - touchEndX;
      onDrag?.(0);

      if (Math.abs(diffX) > threshold) {
        if (diffX > 0 && onSwipeLeft) {
          onSwipeLeft();
        } else if (diffX < 0 && onSwipeRight) {
          onSwipeRight();
        }
      }
    },
    [onSwipeLeft, onSwipeRight, threshold, onDrag],
  );

  return { handleTouchStart, handleTouchMove, handleTouchEnd };
};

const SWIPE_DIRECTION = {
  LEFT: 'left',
  RIGHT: 'right',
} as const;
const MAX_OPACITY = 0.8;
const DRAG_RATIO = 100;
const calculateOpacity = (dragValue: number) =>
  dragValue > 0 ? Math.min(dragValue / DRAG_RATIO, MAX_OPACITY) : 0;

export const SWIPE_INDICATORS = [
  {
    direction: SWIPE_DIRECTION.LEFT,
    getOpacity: (dragX: number) => calculateOpacity(dragX),
  },

  {
    direction: SWIPE_DIRECTION.RIGHT,
    getOpacity: (dragX: number) => calculateOpacity(-dragX),
  },
] as const;
